// Хүүхэд (эцэг эх) — /child: хүүхдийн нэрээр таб, hero (хаус, ирц, house point), ирцийн дэлгэрэнгүй, өдөр өнжүүлэх.
// Нүүр зөвхөн эцэг эхийнх; хүүхдийн мэдээлэл энд. Ирц /guardian/home-оос. Бага ангийн хүүхэд: v0.1 загвар (pages/Elem/ElemChildPage).
import React, { useEffect, useMemo, useState } from "react";
import { Spin, message } from "antd";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiRefreshCw, FiCalendar } from "react-icons/fi";
import dayjs from "dayjs";
import { instance } from "utils/axios";
import DaycareCard from "./DaycareCard";
import ElemChildPage from "pages/Elem/ElemChildPage";
import { isElem } from "pages/Elem/shared";
import AttendanceDays, { ST, StatusChips, HAS_TIME, DOW_FULL } from "./AttendanceDays";
import { SectionHead } from "./ParentDashboard";
import { LearningOverview } from "pages/Study";
import { HouseMark } from "components/brand";

const presentPct = (c) => {
  const present = (c.month.counts["Ирсэн"] || 0) + (c.month.counts["Хоцорсон"] || 0);
  return c.month.total ? Math.round((present * 100) / c.month.total) : null;
};

// ── Хүүхдийн hero (ягаан градиент) ──
function ChildHero({ c, month, children, onPick }) {
  const [houseOk, setHouseOk] = useState(true);
  const [imgOk, setImgOk] = useState(true);
  const pct = presentPct(c);
  // Энэ 7 хоногийн ирц (Даваагаас) — сонгосон сар энэ сар байх үед л
  const week = useMemo(() => {
    const mon = dayjs().subtract((dayjs().day() + 6) % 7, "day").format("YYYY-MM-DD");
    const days = month === dayjs().format("YYYY-MM") ? (c.month.days || []).filter((d) => String(d.date) >= mon) : [];
    return { total: days.length, present: days.filter((d) => HAS_TIME.includes(d.status)).length };
  }, [c, month]);
  const todayText = c.today ? c.today.status : "Бүртгэгдээгүй";
  const todayTime = c.today?.time && HAS_TIME.includes(c.today.status) ? dayjs(c.today.time).format("HH:mm") : null;
  // Хүүхдийн зураг гол; хаус — буланд тэмдэг. Зураггүй бол хаусын лого, тэр ч үгүй бол эхний үсэг
  const badge = c.imageUrl && imgOk
    ? <img src={c.imageUrl} alt={c.firstName} referrerPolicy="no-referrer" onError={() => setImgOk(false)} className="h-full w-full object-cover" />
    : c.house?.image && houseOk
      ? <img src={c.house.image} alt={`${c.house.name} хаус`} referrerPolicy="no-referrer" onError={() => setHouseOk(false)} className="h-full w-full object-contain p-1.5" />
      : <span className="text-2xl md:text-3xl font-extrabold">{(c.firstName || "?").charAt(0)}</span>;
  // Тоо/цаг томоор, үг (ж: "Бүртгэгдээгүй") арай жижгээр; onClick-тэй бол товч (ирцийн дэлгэрэнгүй нээнэ)
  const stat = (big, small, onClick) => {
    const Tag = onClick ? "button" : "div";
    return (
      <Tag {...(onClick ? { type: "button", onClick, "aria-label": `${small} — дэлгэрэнгүй` } : {})}
        className={`md:min-w-[132px] md:px-[18px] md:py-3 md:rounded-[14px] md:bg-white/[.12] flex flex-col items-end text-white ${onClick ? "px-3 py-1.5 rounded-xl bg-white/[.12] hover:bg-white/20 md:hover:bg-white/20 transition-colors cursor-pointer" : ""}`}>
        <span className={`font-extrabold leading-tight ${/\d|—/.test(big) ? "text-xl md:text-[26px]" : "text-base md:text-lg"}`}>{big}</span>
        <span className="text-xs md:text-[13px] text-[#e5d8f5] whitespace-nowrap inline-flex items-center gap-0.5">{small}{onClick && <FiChevronRight size={13} />}</span>
      </Tag>
    );
  };

  return (
    <section aria-label="Хүүхдийн мэдээлэл"
      className="rounded-[18px] md:rounded-[20px] p-3.5 md:px-7 md:py-6 text-white flex flex-wrap items-center gap-3 md:gap-x-7 md:gap-y-5"
      style={{ background: "linear-gradient(120deg, #56198c 0%, #6b27ac 55%, #7c32c3 100%)" }}>
      {/* Олон хүүхэдтэй бол нэрээр нь сэлгэнэ */}
      {children.length > 1 && (
        <div role="tablist" aria-label="Хүүхэд сонгох" className="w-full hscroll -mb-0.5">
          {children.map((k) => {
            const on = String(k.id) === String(c.id);
            return (
              <button key={k.id} type="button" role="tab" aria-selected={on} onClick={() => onPick(k.id)}
                className={`h-9 px-4 rounded-full text-sm font-bold whitespace-nowrap shrink-0 transition-colors ${on ? "bg-white text-primary" : "bg-white/[.12] text-white hover:bg-white/20"}`}>
                {k.firstName}
              </button>
            );
          })}
        </div>
      )}
      <span className="relative shrink-0">
        <span className="h-[52px] w-[52px] md:h-[84px] md:w-[84px] rounded-xl md:rounded-[18px] border border-white/35 bg-white/10 overflow-hidden flex items-center justify-center">{badge}</span>
        {c.imageUrl && imgOk && c.house?.name && <HouseMark house={c.house.name} size={26} className="absolute -right-2 -bottom-2 ring-2 ring-white/70" />}
      </span>
      <div className="flex-1 md:flex-[1_1_260px] min-w-0 flex flex-col md:gap-0.5">
        <span className="text-[13px] md:text-[15px] text-[#e5d8f5] truncate">{c.lastName}</span>
        <span className="text-xl md:text-[30px] font-extrabold leading-tight md:tracking-[-0.01em] truncate">{c.firstName}</span>
        <span className="text-[13px] md:text-[15px] text-[#e5d8f5] truncate">{[c.code, c.grade ? `${c.grade}-р анги` : null, c.house ? c.house.monName || c.house.name : null].filter(Boolean).join(" · ")}</span>
      </div>
      <div className="flex gap-3 shrink-0">
        {week.total
          ? stat(`${week.present}/${week.total}`, "ирц · энэ 7 хоног")
          : stat(todayTime || todayText, todayTime ? `${todayText} · өнөөдөр` : "ирц · өнөөдөр")}
        <div className="hidden md:flex">{stat(pct == null ? "—" : `${pct}%`, `ирц · ${dayjs(`${month}-01`).format("M")}-р сар`)}</div>
        {c.house && c.house.point != null && (
          <div className="hidden md:flex">{stat(String(c.house.point), `house point${c.house.place ? ` · ${c.house.place}-р байр` : ""}`)}</div>
        )}
      </div>
    </section>
  );
}

// ── Ирцийн дэлгэрэнгүй: өнөөдөр, сарын нэгтгэл + өдрүүд, өдөр өнжүүлэх ──
function AttendanceDetail({ c, month, setMonth, today, loading, onRefresh }) {
  const pct = presentPct(c);
  const todayStyle = c.today ? ST[c.today.status] : null;
  const isCurrentMonth = month === dayjs().format("YYYY-MM");
  const navBtn = "h-9 w-9 rounded-full border border-[#e9e8f0] bg-white flex items-center justify-center text-[#6e5e8a] hover:bg-[#faf7ff]";

  return (
    <section aria-labelledby="att-h" className="tj-card px-4 pt-[18px] pb-4 md:p-6">
      <SectionHead title={<span id="att-h">Ирц</span>}
        sub={[`${dayjs(`${month}-01`).format("M")}-р сар`, pct == null ? null : `ирц ${pct}%`].filter(Boolean).join(" · ")}
        right={
          <div className="flex items-center gap-1 shrink-0">
            <button aria-label="Өмнөх сар" className={navBtn} onClick={() => setMonth(dayjs(`${month}-01`).subtract(1, "month").format("YYYY-MM"))}><FiChevronLeft /></button>
            <span className="text-sm font-semibold w-[68px] text-center text-[#1c1c1c]">{dayjs(`${month}-01`).format("YYYY.MM")}</span>
            <button aria-label="Дараагийн сар" disabled={isCurrentMonth} className={`${navBtn} disabled:text-[#e5d8f5] disabled:hover:bg-white`}
              onClick={() => setMonth(dayjs(`${month}-01`).add(1, "month").format("YYYY-MM"))}><FiChevronRight /></button>
            <button aria-label="Шинэчлэх" className={navBtn} onClick={onRefresh}><FiRefreshCw className={loading ? "animate-spin" : ""} /></button>
          </div>
        } />

      <div className={`mt-2 rounded-[14px] px-4 py-3 flex items-center gap-3 ${c.today ? todayStyle?.soft || "bg-[#faf7ff]" : "bg-[#faf7ff] text-[#6e5e8a]"}`}>
        <div className="flex-1 min-w-0">
          <div className="text-xs opacity-70">Өнөөдөр · {dayjs().format("MM.DD")} {DOW_FULL[dayjs().day()]}</div>
          <div className="text-lg font-bold leading-tight">{c.today ? c.today.status : "Бүртгэгдээгүй"}</div>
        </div>
        {c.today?.time && HAS_TIME.includes(c.today.status) && (
          <div className="text-right">
            <div className="text-xs opacity-70">ирсэн цаг</div>
            <div className="text-2xl font-extrabold leading-none tabular-nums">{dayjs(c.today.time).format("HH:mm")}</div>
          </div>
        )}
      </div>

      <div className="mt-4">
        {c.month.total ? (
          <>
            <div className="mb-3"><StatusChips counts={c.month.counts} /></div>
            <AttendanceDays days={c.month.days} today={today} />
          </>
        ) : (
          <div className="text-sm text-[#6e5e8a] py-3 text-center">Энэ сард ирцийн бүртгэл алга</div>
        )}
      </div>

      {/* Өдөр өнжүүлэх — зөвхөн бага ангийн хүүхэдтэй бол гарна */}
      <DaycareCard studentId={c.id} />
    </section>
  );
}

function ChildPage() {
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // ?c=<id> — төслийн хуудас, нүүрнээс ирэхэд тэр хүүхэд сонгогдоно
  const [childId, setChild] = useState(() => new URLSearchParams(window.location.search).get("c"));
  // Сонгосон хүүхдийг URL-д (?c=) хадгална — хичээлээс "Буцах" дарахад тэр хүүхэд дээрээ буцаж ирнэ
  const setChildId = (id) => {
    setChild(id);
    const u = new URL(window.location.href);
    u.searchParams.set("c", id);
    window.history.replaceState(window.history.state, "", u);
  };

  const load = (m = month) => {
    setLoading(true);
    instance({ method: "get", url: "/guardian/home", params: { month: m } })
      .then((res) => setData(res.data))
      .catch(() => message.error("Мэдээлэл татаж чадсангүй"))
      .then(() => setLoading(false));
  };
  useEffect(() => { load(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const children = data?.children || [];
  const child = children.find((c) => String(c.id) === String(childId)) || children[0];

  return (
    <div className="pt-3.5 md:pt-7 pb-6 md:pb-14 max-w-[1180px] mx-auto flex flex-col gap-3.5 md:gap-5">
      {loading && !data ? (
        <div className="flex justify-center py-10"><Spin /></div>
      ) : child && isElem(child.grade) ? (
        <ElemChildPage child={child} kids={children} onPick={setChildId} />
      ) : child ? (
        <>
          <ChildHero c={child} month={month} children={children} onPick={setChildId} />
          {/* Хүүхдийн сурлага — сурагчийн "Сурлага" хуудастай ижил */}
          <section className="flex flex-col gap-3">
            <SectionHead eyebrow="Сурлага" title={`${child.firstName}-ийн сурлагын явц`} />
            <LearningOverview key={child.id} who={child.id} />
          </section>
          <div className="flex flex-wrap gap-3.5 md:gap-5 items-start">
            <div className="flex-[1.618_1_440px] min-w-0">
              <AttendanceDetail c={child} month={month} setMonth={setMonth} today={data?.today} loading={loading} onRefresh={() => load()} />
            </div>
            <div className="flex-[1_1_320px] min-w-0">
              <Link to="/schedule" className="tj-card flex items-center gap-3 p-4 md:p-5 text-[#1c1c1c] hover:text-[#1c1c1c] hover:bg-[#faf7ff]">
                <span className="h-11 w-11 rounded-xl bg-[#f3eeff] text-primary flex items-center justify-center shrink-0"><FiCalendar size={20} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-base font-bold">Хичээлийн хуваарь</span>
                  <span className="block text-sm text-[#6e5e8a]">{child.firstName}-ийн 7 хоногийн хуваарь</span>
                </span>
                <FiChevronRight size={20} className="text-primary shrink-0" />
              </Link>
            </div>
          </div>
        </>
      ) : (
        <section className="tj-card p-6 text-center text-[#6e5e8a] text-sm">
          Таны бүртгэлтэй хүүхэд олдсонгүй. Сургуулийн захиргаатай холбогдоно уу.
        </section>
      )}
    </div>
  );
}

export default ChildPage;
